import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { ExclusivityTeamMember, ExclusivityTerms, ExclusivityView } from '../api/types';
import { formatDateOnly } from '../lib/date-only';
import {
  addCalendarDays,
  EXCLUSIVITY_MAX_DAYS,
  EXCLUSIVITY_MIN_DAYS,
  EXCLUSIVITY_QUICK_PICK_DAYS,
  exclusivityTermsProblem,
  formatRemaining,
  remainingUntil,
  todayInZone,
} from '../lib/exclusivity';
import { cn } from '../lib/utils';
import { Button } from './button';
import { ConfirmDialog } from './confirm-dialog';
import { DecisionConfirmDialog, extensionCopy } from './decision-confirm';
import { Dialog } from './dialog';
import { LegendWithAside } from './legend-aside';
import { SellerVotePanel } from './seller-vote';

/**
 * Enforced exclusivity UI (ADR-0046), shared by the admin LOI view, the seller
 * portal and the buyer room: the accept dialog (warning + terms), the countdown
 * clock, the admin / seller status panel (validate, extend, end) and the paused
 * buyer's notice. One vocabulary so the three apps never describe the same
 * exclusivity differently. Presentational — pages own the mutations.
 */

/** The day the period ends ("runs through"), from the first day not covered. */
function lastDay(expiresOn: string): string {
  return formatDateOnly(addCalendarDays(expiresOn, -1));
}

/**
 * The countdown clock: days / hours (minutes in the last day) left until the
 * exclusivity ends at 00:00 on `expiresOn` in the room's timezone. Ticks every
 * 30 seconds.
 */
export function ExclusivityCountdown({
  endsAt,
  expiresOn,
  timezone,
  label = 'Exclusivity',
  labelAside,
  className,
}: {
  endsAt: string;
  expiresOn: string;
  timezone: string;
  label?: string;
  /** A "?" help tip shown after the label. */
  labelAside?: ReactNode;
  className?: string;
}) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const r = remainingUntil(endsAt, now);
  return (
    <div
      className={cn('rounded-md border bg-surface-2 px-3 py-2', className)}
      data-testid="exclusivity-countdown"
    >
      <div className="flex items-center gap-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        {labelAside}
      </div>
      <p className="font-serif text-2xl font-semibold tabular-nums" role="timer" aria-live="off">
        {r.expired ? 'Ended' : `${formatRemaining(r)} left`}
      </p>
      <p className="text-xs text-muted-foreground">
        Runs through {lastDay(expiresOn)} ({timezone})
      </p>
    </div>
  );
}

/** The plain-English consequences shown before an accept takes effect. */
function AcceptConsequences({
  sellerValidationRequired,
  viewer,
}: {
  sellerValidationRequired: boolean;
  viewer: 'intermediary' | 'seller';
}) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-sm text-foreground">
      {sellerValidationRequired ? (
        <li>
          <strong>The owners must approve this acceptance.</strong> It is only a proposal: nothing
          below happens until owners holding more than 50% of the company approve it — they are
          asked by email and in their portal.
        </li>
      ) : null}
      {viewer === 'seller' ? (
        <li>
          <strong>Your acceptance is your vote.</strong> With one owner at 100% it takes effect at
          once; otherwise it takes effect as soon as owners holding more than 50% of the company
          approve it.
        </li>
      ) : null}
      <li>
        Every other buyer in this data room is told that an LOI has been tentatively accepted, and
        their <strong>data room access is paused</strong> until closing or the exclusivity period
        ends. Their access is not revoked — it comes back on its own when the period ends.
      </li>
      <li>
        A countdown to the end of the exclusivity period appears for the intermediary and the owners
        (and for buyers only if the room&apos;s settings show it to them).
      </li>
      <li>
        {viewer === 'seller'
          ? 'Any owner can extend the period, and so can the intermediary with the owners’ approval; buyers cannot.'
          : 'You can propose an extension, which takes effect once the owners approve it; buyers cannot.'}
      </li>
    </ul>
  );
}

/**
 * Who keeps access with the winning buyer: their NAMED team (colleagues and
 * outside advisers of any email domain, named by the buyer or the
 * intermediary). The intermediary sees who (`team`); the seller only how many
 * (`teamSize`) — buyer identities never reach the seller beyond the LOI sender.
 */
export function TeamLine({
  team,
  teamSize,
}: {
  team: readonly ExclusivityTeamMember[] | null | undefined;
  teamSize: number | null | undefined;
}) {
  const size = team ? team.length : (teamSize ?? 0);
  return (
    <p className="text-sm" data-testid="exclusivity-team">
      <span className="font-medium">Also keeps access: </span>
      {size === 0
        ? 'no one else — the buyer has not named a team.'
        : team
          ? `${team.map((m) => m.email ?? m.actorId).join(', ')} (the buyer’s team).`
          : `${size} ${size === 1 ? 'person' : 'people'} on the buyer’s team.`}
    </p>
  );
}

/**
 * The ACCEPT dialog: a plain warning of what accepting does, then the required
 * exclusivity terms — length (30 / 60 / 90 / 120-day quick picks or a custom
 * 1–365) and the start date (default today in the room's timezone) — and who
 * keeps access: the buyer and their named team (names for the intermediary, a
 * count for the seller). The team itself is managed on the buyer, not here.
 */
export function AcceptLoiDialog({
  buyerLabel,
  team,
  teamSize,
  timezone,
  sellerValidationRequired,
  viewer,
  pending = false,
  error,
  onConfirm,
  onClose,
  legendAside,
}: {
  buyerLabel: string;
  /** The buyer's named team (intermediary only); null/absent for the seller. */
  team?: readonly ExclusivityTeamMember[] | null;
  /** How many are on the buyer's team (when `team` is not shown). */
  teamSize?: number | null;
  timezone: string;
  sellerValidationRequired: boolean;
  viewer: 'intermediary' | 'seller';
  pending?: boolean;
  error?: string | null;
  onConfirm: (terms: ExclusivityTerms) => void;
  onClose: () => void;
  /** A "?" help tip shown after the "Exclusivity period" legend. */
  legendAside?: ReactNode;
}) {
  const today = todayInZone(timezone);
  const [lengthDays, setLengthDays] = useState<number>(60);
  const [custom, setCustom] = useState('');
  const [startDate, setStartDate] = useState(today);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const lengthId = useId();
  const startId = useId();
  const length = custom !== '' ? Number(custom) : lengthDays;
  const problem = exclusivityTermsProblem(startDate, length, today);
  const expiresOn = problem ? null : addCalendarDays(startDate, length);

  return (
    <Dialog
      role="alertdialog"
      size="md"
      title="Accept this LOI?"
      description={`Letter of intent from ${buyerLabel}`}
      onClose={onClose}
      busy={pending}
      initialFocusRef={cancelRef}
      footer={
        <div className="ml-auto flex gap-2">
          <Button ref={cancelRef} variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={pending || problem !== null}
            onClick={() => onConfirm({ startDate, lengthDays: length })}
          >
            {pending
              ? 'Saving…'
              : sellerValidationRequired
                ? 'Accept and send to the owners'
                : 'Accept and start exclusivity'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="rounded-md border border-st-check-line bg-st-check p-3 text-st-check-fg">
          <p className="mb-2 text-sm font-semibold">What happens when you accept</p>
          <AcceptConsequences sellerValidationRequired={sellerValidationRequired} viewer={viewer} />
        </div>

        <fieldset className="space-y-2">
          <LegendWithAside id={lengthId} className="text-sm font-medium" aside={legendAside}>
            Exclusivity period
          </LegendWithAside>
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-labelledby={lengthId}
          >
            {EXCLUSIVITY_QUICK_PICK_DAYS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={custom === '' && lengthDays === n}
                onClick={() => {
                  setCustom('');
                  setLengthDays(n);
                }}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm font-medium',
                  custom === '' && lengthDays === n
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'hover:bg-muted',
                )}
              >
                {n} days
              </button>
            ))}
            <label className="flex items-center gap-1.5 text-sm">
              <span className="text-muted-foreground">Custom</span>
              <input
                type="number"
                inputMode="numeric"
                min={EXCLUSIVITY_MIN_DAYS}
                max={EXCLUSIVITY_MAX_DAYS}
                value={custom}
                placeholder="days"
                aria-label="Custom length in days"
                onChange={(e) => setCustom(e.target.value)}
                className="w-20 rounded-md border bg-background px-2 py-1 text-sm"
              />
            </label>
          </div>
        </fieldset>

        <div className="space-y-1.5">
          <label htmlFor={startId} className="text-sm font-medium">
            Starts on
          </label>
          <input
            id={startId}
            type="date"
            min={today}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="block rounded-md border bg-background px-2 py-1 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Dates are in the room&apos;s timezone ({timezone}).
          </p>
        </div>

        <section aria-label="Who keeps access" className="space-y-1 rounded-md border p-3">
          <p className="text-sm font-medium">Who keeps access</p>
          <p className="text-sm">
            <strong>{buyerLabel}</strong>
            {viewer === 'intermediary' && team && team.length > 0
              ? ' and their team:'
              : (teamSize ?? 0) > 0
                ? ` and ${teamSize} ${teamSize === 1 ? 'person' : 'people'} on their team.`
                : ' only — they have not named a team.'}
          </p>
          {viewer === 'intermediary' && team && team.length > 0 ? (
            <ul className="list-disc pl-5 text-sm">
              {team.map((m) => (
                <li key={m.actorId}>{m.email ?? m.actorId}</li>
              ))}
            </ul>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {viewer === 'intermediary'
              ? 'Everyone else is paused. To change the team, open this buyer under Buyers (Team).'
              : 'Everyone else is paused. The buyer and the intermediary manage the team.'}
          </p>
        </section>

        <p className="text-sm" aria-live="polite">
          {problem ? (
            <span className="text-destructive">{problem}</span>
          ) : (
            <>
              Exclusive from <strong>{formatDateOnly(startDate)}</strong> through{' '}
              <strong>{lastDay(expiresOn!)}</strong> ({length} days).
            </>
          )}
        </p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * The admin / owner exclusivity panel: a pending acceptance (the owners vote; the
 * intermediary waits), or the in-effect period with its countdown, a pending
 * extension (the owners vote), the extend control (an owner while they may respond
 * to LOIs; the intermediary, as a PROPOSAL, only in a room it runs) behind an "Are you
 * sure?" and — for the admin — End exclusivity. Nothing here is ever rendered for
 * a buyer.
 */
export function ExclusivityPanel({
  view,
  viewer,
  canExtend,
  pending = false,
  error,
  onValidate,
  onValidateExtension,
  onExtend,
  onEnd,
  countdownLabelAside,
}: {
  view: ExclusivityView;
  viewer: 'intermediary' | 'seller';
  canExtend: boolean;
  pending?: boolean;
  error?: string | null;
  onValidate?: (decision: 'validate' | 'decline') => void;
  onValidateExtension?: (decision: 'validate' | 'decline') => void;
  onExtend?: (additionalDays: number) => void;
  onEnd?: () => void;
  /** A "?" help tip shown after the countdown's label. */
  countdownLabelAside?: ReactNode;
}) {
  const [confirm, setConfirm] = useState<'end' | 'extend' | null>(null);
  const [days, setDays] = useState('30');
  const excl = view.exclusivity;
  if (view.state === 'none' || !excl) return null;
  const buyer = view.buyerEmail ?? 'the buyer';
  // Only the owners vote (never the intermediary, never the seller's team).
  const canValidate = viewer === 'seller' && view.viewerIsValidator;
  const vote = view.sellerVote ?? null;
  const castVote = (handler: ((d: 'validate' | 'decline') => void) | undefined) =>
    handler
      ? (v: 'approve' | 'decline') => handler(v === 'approve' ? 'validate' : 'decline')
      : undefined;
  const teamBlock = <TeamLine team={view.team} teamSize={view.teamSize} />;

  if (view.state === 'pending_validation' && vote && vote.kind === 'acceptance') {
    return (
      <div className="space-y-3">
        <SellerVotePanel
          decision={vote}
          title="Accepted — pending the owners’ vote"
          pending={pending}
          error={error}
          onVote={canValidate ? castVote(onValidate) : undefined}
        />
        <TeamLine team={view.team} teamSize={view.teamSize} />
      </div>
    );
  }

  if (view.state === 'pending_validation') {
    // The owners' vote is not loaded (yet): the status alone, no controls.
    return (
      <section
        aria-label="Accepted LOI awaiting the owners’ vote"
        className="space-y-3 rounded-md border border-st-check-line bg-st-check p-4 text-st-check-fg"
      >
        <p className="text-sm font-semibold">Accepted — pending the owners’ vote</p>
        <p className="text-sm">
          The LOI from <strong>{buyer}</strong> was accepted with {excl.lengthDays} days of
          exclusivity from {formatDateOnly(excl.startDate)} through {lastDay(excl.expiresOn)} (
          {excl.timezone}). It takes effect when owners holding more than 50% of the company approve
          it. No buyer is paused until then.
        </p>
        {teamBlock}
      </section>
    );
  }

  const extension = excl.pendingExtensionDays;
  const daysNum = Number(days);
  const daysValid =
    Number.isInteger(daysNum) && daysNum >= EXCLUSIVITY_MIN_DAYS && daysNum <= EXCLUSIVITY_MAX_DAYS;
  return (
    <section aria-label="Exclusivity in effect" className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold">Exclusivity in effect</p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">{buyer}</strong> holds exclusivity. Every other
            buyer&apos;s data room access is paused until closing or the period ends.
          </p>
          {teamBlock}
        </div>
        {view.endsAt && view.expiresOn ? (
          <ExclusivityCountdown
            endsAt={view.endsAt}
            expiresOn={view.expiresOn}
            timezone={view.timezone}
            labelAside={countdownLabelAside}
          />
        ) : null}
      </div>

      {extension !== null && vote && vote.kind === 'extension' ? (
        <SellerVotePanel
          decision={vote}
          title={`Extension of ${extension} days — pending the owners’ vote`}
          pending={pending}
          error={error}
          onVote={canValidate ? castVote(onValidateExtension) : undefined}
        />
      ) : extension !== null ? (
        <p className="rounded-md border border-st-check-line bg-st-check p-3 text-sm text-st-check-fg">
          <strong>Extension of {extension} days</strong> — waiting for the owners’ vote.
        </p>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        {canExtend && onExtend && extension === null ? (
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (daysValid) setConfirm('extend');
            }}
          >
            <label className="space-y-1 text-sm">
              <span className="block font-medium">Extend by (days)</span>
              <input
                type="number"
                min={EXCLUSIVITY_MIN_DAYS}
                max={EXCLUSIVITY_MAX_DAYS}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="w-24 rounded-md border bg-background px-2 py-1 text-sm"
              />
            </label>
            <Button type="submit" size="sm" variant="outline" disabled={pending || !daysValid}>
              {viewer === 'intermediary' ? 'Propose extension' : 'Extend'}
            </Button>
          </form>
        ) : null}
        {viewer === 'intermediary' && onEnd ? (
          <Button
            size="sm"
            variant="danger-ghost"
            disabled={pending}
            onClick={() => setConfirm('end')}
          >
            End exclusivity
          </Button>
        ) : null}
      </div>
      {viewer === 'intermediary' && canExtend ? (
        <p className="text-xs text-muted-foreground">
          Extensions you enter are proposals: they take effect when the owners approve them.
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {confirm === 'extend' && onExtend ? (
        <DecisionConfirmDialog
          copy={extensionCopy(buyer, daysNum, viewer === 'intermediary' ? 'intermediary' : 'owner')}
          pending={pending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            onExtend(daysNum);
          }}
        />
      ) : null}
      {confirm === 'end' && onEnd ? (
        <ConfirmDialog
          title="End exclusivity now?"
          confirmLabel="End exclusivity"
          danger
          pending={pending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            onEnd();
            setConfirm(null);
          }}
        >
          <p>
            The other buyers&apos; data room access resumes immediately and they are told it has
            resumed. The owners and {buyer} are notified. This is recorded in the audit log.
          </p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

/**
 * What a BUYER sees about an exclusivity: a paused buyer gets the notice (and
 * the countdown when the room shows it to them); the exclusive buyer gets the
 * countdown only when the room shows it to them. Null otherwise.
 */
export function BuyerExclusivityNotice({
  view,
  titleAside,
  clockLabelAside,
}: {
  view: ExclusivityView | null | undefined;
  /** A "?" help tip shown after the "Data room access is paused" heading. */
  titleAside?: ReactNode;
  /** A "?" help tip shown after the clock's label. */
  clockLabelAside?: ReactNode;
}) {
  if (!view || view.state !== 'in_effect') return null;
  const clock =
    view.clockVisible && view.endsAt && view.expiresOn ? (
      <ExclusivityCountdown
        endsAt={view.endsAt}
        expiresOn={view.expiresOn}
        timezone={view.timezone}
        label={view.paused ? 'Exclusivity period' : 'Your exclusivity'}
        labelAside={clockLabelAside}
      />
    ) : null;
  if (view.paused) {
    return (
      <section
        role="status"
        aria-label="Data room access paused"
        className="space-y-3 rounded-lg border border-st-check-line bg-st-check p-5 text-st-check-fg"
      >
        <div className="flex items-center gap-1.5">
          <h2 className="font-serif text-xl font-semibold">Data room access is paused</h2>
          {titleAside}
        </div>
        <p className="text-sm">
          An LOI has been tentatively accepted. Data room access is paused until closing or the
          exclusivity period ends. Your access has not been removed — you will be notified when it
          resumes.
        </p>
        {clock}
      </section>
    );
  }
  return clock;
}
